import { z } from 'zod'
import { browserEnvironmentSchema } from '../common/browser-environment'
import { AdminApiError, request } from '../api/client'

const summarySchema = z.object({
  browserEnvironment: browserEnvironmentSchema.nullable().default(null),
  id: z.string(), userId: z.string(), student: z.string(), email: z.string(),
  clientId: z.string(), client: z.string(), programId: z.string().nullable(), program: z.string().nullable(),
  scenarioId: z.string(), tutorId: z.string(), tutor: z.string().nullable(), lesson: z.string().nullable(),
  tutorContentKey: z.string().nullable().default(null),
  lessonRevisionId: z.string(), tutorRevisionId: z.string(),
  historicalWordingExact: z.boolean(), startedAt: z.string().nullable(), endedAt: z.string().nullable(),
  durationMs: z.number().nullable(), mode: z.string(), scenarioType: z.string(), endReason: z.string().nullable(), transcriptStatus: z.string().nullable(),
})
const replaySchema = z.object({
  mode: z.enum(['committed_turns', 'sequence_only', 'unavailable']), reason: z.string().nullable(), durationMs: z.number().nullable(),
  lines: z.array(z.object({ index: z.number(), speaker: z.enum(['student', 'coach']), text: z.string(), offsetMs: z.number().nullable() })),
})
const evaluationSchema = z.object({
  postCallFailedAt: z.string().nullable(),
  skill: z.object({
    scorePercent: z.number().nullable(), isPass: z.boolean(), meaningfulAttempt: z.boolean(),
    fluencyScore: z.number().nullable(), confidenceOk: z.boolean(), languageOk: z.boolean(),
    rubricVersion: z.string(), judgeVersion: z.string(), evaluatedAt: z.string(), componentsAvailable: z.boolean(),
    components: z.array(z.object({ key: z.string(), kind: z.enum(['functional', 'shared']), score: z.number().nullable(), notElicited: z.boolean(), evidence: z.string().nullable(), evidenceVerified: z.boolean() })),
  }).nullable(),
  scoring: z.array(z.object({ cycleIndex: z.number(), points: z.number(), activeSeconds: z.number(), functionalUsageScore: z.number().nullable(), correctnessScore: z.number().nullable(), terminalMarker: z.boolean(), schemeVersion: z.number() })),
  placement: z.object({ status: z.string(), recommendedCefrLevel: z.string().nullable(), completedAt: z.string(), lowConfidence: z.boolean(), reviewRequired: z.boolean(), needsPaceReview: z.boolean(), contributingCalls: z.number() }).nullable(),
})
export type CallEvaluation = z.infer<typeof evaluationSchema>
const detailSchema = z.object({ session: summarySchema, scenarioLevel: z.string().nullable().default(null), enrollmentId: z.string().nullable().default(null), cohortId: z.string().nullable().default(null), cohort: z.string().nullable().default(null), replay: replaySchema, contentError: z.string().nullable(), evaluation: evaluationSchema })
const listSchema = z.object({ items: z.array(summarySchema), total: z.number(), page: z.number(), limit: z.number() })
const optionsSchema = z.object({ items: z.array(z.object({ id: z.string(), label: z.string(), context: z.string().nullable() })), hasMore: z.boolean() })
export type Replay = z.infer<typeof replaySchema>
export type SessionDetail = z.infer<typeof detailSchema>
export type SessionSummary = z.infer<typeof summarySchema>
export type SessionOption = z.infer<typeof optionsSchema>['items'][number]
export type OptionKind = 'students' | 'programs' | 'tutors'
export type SessionParams = { userId?: string; programId?: string; tutorId?: string; isInternal?: boolean; search?: string; fromDate?: string; toDate?: string; sortDirection?: 'asc' | 'desc'; page?: number }
function query(params: object): string {
  return new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== '').map(([key, value]) => [key, String(value)])).toString()
}
export const sessionsClient = {
  list: async (params: SessionParams) => listSchema.parse(await request<unknown>(`/coach/admin/sessions?${query(params)}`)),
  detail: async (id: string, userId?: string, signal?: AbortSignal) => detailSchema.parse(await request<unknown>(`/coach/admin/sessions/${encodeURIComponent(id)}?${query({ userId })}`, { cache: 'no-store', signal })),
  options: async (kind: OptionKind, params: { q?: string; id?: string }) => optionsSchema.parse(await request<unknown>(`/coach/admin/sessions/options/${kind}?${query(params)}`)),
}
export function sessionError(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.detail === 'session_not_found') return 'This session is unavailable or has been deleted.'
    if (error.detail === 'session_audit_unavailable') return 'Access could not be recorded. Try opening the transcript again.'
    if (error.status === 422) return 'Check the selected filters and date range, then try again.'
    if (error.status === 403) return 'An internal admin account is required to inspect sessions.'
  }
  return 'Could not load session data. Try again.'
}
export function transcriptStatus(value: string | null): string {
  return ({ persisted: 'Saved', pending: 'Pending', failed: 'Delivery failed', empty: 'Empty' })[value ?? ''] ?? 'Not recorded'
}
export function clockLabel(ms: number): string {
  const seconds = Math.floor(Math.max(0, ms) / 1000)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}
export function sessionDate(value: string | null): string {
  return value ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Tokyo', year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : 'Not recorded'
}

export function sessionOutcome(reason: string | null, ended: string | null): string {
  const value = reason?.replaceAll('_', ' ')
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : ended ? 'Ended' : 'End not recorded'
}

/** Content destinations show the current catalog entry, retaining the call-time label. */
export function sessionScenarioHref(session: SessionSummary): string | null {
  if (session.scenarioType === 'onboarding') return session.programId ? `/admin/content/diagnostics/${encodeURIComponent(session.programId)}` : null
  return `/admin/content/scenarios/${encodeURIComponent(session.scenarioId)}`
}

export const SESSION_FILTERS = ['userId', 'programId', 'tutorId', 'isInternal', 'search', 'fromDate', 'toDate'] as const
export function sessionListSearch(source: URLSearchParams): string {
  const result = new URLSearchParams()
  for (const key of [...SESSION_FILTERS, 'sortDirection', 'page']) { const value = source.get(key); if (value) result.set(key, value) }
  return result.size ? `?${result.toString()}` : ''
}
